import type { ModalProps, SelectProps } from "antd";

/**
 * Viewport-safe form dialogs: fixed header/footer, scrollable body, borders
 * and focus rings not clipped by overflow. Applied globally via ConfigProvider;
 * pass again on modals that override `styles` so merges stay complete.
 */
export const MODAL_FORM_BODY_STYLES: ModalProps["styles"] = {
  content: {
    maxHeight: "calc(100dvh - 2rem)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  header: {
    flex: "none",
    marginBottom: 0,
    paddingTop: 14,
    paddingBottom: 0,
    paddingInline: 16,
    borderBottom: "none",
  },
  body: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    overscrollBehavior: "contain",
    paddingTop: 12,
    paddingBottom: 8,
    paddingInline: 16,
  },
  footer: {
    flex: "none",
    marginTop: 0,
    paddingInline: 16,
    paddingBottom: 16,
    paddingTop: 12,
  },
};

export const MODAL_FORM_CLASS_NAMES: ModalProps["classNames"] = {
  content: "tnn-modal-form-content",
  header: "tnn-modal-form-header",
  body: "tnn-modal-form-body scrollbar-standard",
};

/**
 * Optional inner wrapper for form field spacing / focus-ring padding.
 * Scrolling is on `.tnn-modal-form-body` (ConfigProvider); do not nest a second scroll region.
 */
export const MODAL_FORM_SCROLL_CLASS = "tnn-modal-form-scroll";

/** Render selects / date pickers in `document.body` so modal `overflow` does not clip popups. */
export function modalFormPopupContainer(): HTMLElement {
  return document.body;
}

/**
 * Selects inside a scrolling modal: mount the menu on `document.body` so it
 * can flip upward, and skip the close animation so it does not cover the next field.
 */
export const MODAL_SELECT_POPUP: Pick<
  SelectProps,
  "getPopupContainer" | "transitionName"
> = {
  getPopupContainer: modalFormPopupContainer,
  transitionName: "",
};

/** Panel class for custom (non-antd) form overlays — border on shell, scroll inside. */
export const OVERLAY_FORM_PANEL_CLASS =
  "tnn-overlay-form-panel flex max-h-[min(90vh,calc(100dvh-2rem))] w-full flex-col overflow-hidden rounded-xl border bg-card";

export const OVERLAY_FORM_BODY_CLASS =
  "min-h-0 flex-1 overflow-y-auto overscroll-contain scrollbar-standard px-4 py-4";
